import React from "react";
import { CircleCheckBig } from "lucide-react";
import ContentsContainer from "../../components/ContentsContainer";

const sections = [
{
    sectionId: "purpose",
    sectionTitle: "1. Purpose",
    searchKeywords: ` 
      hyvid employee referral program qualified candidate suitable
      internal team  operation growth workforce policy status successful    
      withdrawal discretion purpose trust rewarding referrals strengthens
      talent pipeline bar hiring quality helps internal teams support
      growing eligible participate candidates vacancies qualify replace
      submit ownership decided rewards paid expected amend suspend withdraw
      program discretion.
    `,
    sectionBody: (
      <>
        <div className="space-y-4 text-sm leading-relaxed">
          <p>
            At Hyvid, we believe some of our best hires come through people who already know us best — our own employees.
          </p>

          <p>
            The Employee Referral Program is our way of encouraging you to recommend great people you trust, and
            rewarding you when those referrals turn into hires. It strengthens our talent pipeline, raises the bar on
            hiring quality, and helps both our internal teams and the teams we support for clients keep growing.         
          </p>

          <div className="space-y-4 text-sm  leading-relaxed">
            <p>This policy walks you through:</p>

            <ul className="list-none space-y-1.5 pl-2">
              {[
                "Who's eligible to participate.",
                "Which candidates and vacancies qualify.",
                "How to submit a referral.",
                "How referral ownership is decided.",
                "When referral rewards are paid.",
                "What's expected of you — and of us.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>
               Like the rest of this handbook, this policy isn't a contract. Hyvid may amend, suspend, replace, or withdraw
               the program at its discretion.
            </p>
          </div>
        </div>
      </>
    ),
  },

  {
    sectionId: "scope",
    sectionTitle: "2. Scope",
    searchKeywords: `
      policy applies eligible employees refer candidates
      vacancies formally identified referral program eligible
      internal corporate positions client-specific client-deployed
      roles permanent full-time part-time positions
      approved fixed-term project-based specialist technical
      senior niche difficult-to-fill high-volume urgent
      recruitment campaigns vacancy qualify reward talent
      acquisition formally transfers existing employee moves
      another team department office function independent
      contractors consultants freelancers interns temporary workers
      agency-supplied personnel external referrers covered unless
      specifically included separate written campaign scope
    `,
    sectionBody: (
 <>
        <div className="space-y-4 text-sm leading-relaxed">
          <p>
            This policy applies to you if you're referring candidates for vacancies that Talent Acquisition has formally
            identified as eligible under the program.
          </p>

          <div className="space-y-4 text-sm  leading-relaxed">
            <p>Eligible vacancies may include:</p>

            <ul className="list-none space-y-1.5 pl-2">
              {[
                "Internal corporate positions.",
                "Client-specific or client-deployed roles.",
                "Permanent full-time or part-time positions.",
                "Approved fixed-term or project-based roles.",
                "Specialist, technical, senior, niche, or difficult-to-fill positions.",
                "High-volume or urgent recruitment campaigns.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p>A vacancy only qualifies for a referral reward once Talent Acquisition has formally flagged it as referral-eligible.</p>
            <p>Internal transfers — where an existing employee moves to another role, team, department, office, or
               function — don't qualify for a referral reward.
            </p>
            <p>Independent contractors, consultants, freelancers, interns, temporary workers, agency-supplied
               personnel, and external referrers aren't covered by this policy, unless a separate written referral campaign
               specifically includes them.
            </p>
          </div>
        </div>
      </>
    ),
  },
  {
  sectionId: "eligibility",
  sectionTitle: "3. Employee Eligibility",
  searchKeywords: `
    active employees employee participate program probationary
    fixed-term project qualify reward particular vacancy
    directly involved sourcing assessing approving hiring
    supervising recruiters talent acquisition responsible
    managers role final decision eligibility regular
    supervise referred candidate senior executives influence
    selection approval another team department office
    function client account direct indirect involvement
    hiring decision 
  `,
  sectionBody: (
    <>
      <div className="space-y-4 text-sm  leading-relaxed">
        <p>The following active employees can take part in the program:</p>

        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Probationary employees",
            "Regular employees",
            "Fixed-term employees",
            "Project employees",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p>
           That said, you won't qualify for a referral reward on a specific vacancy if you're directly involved in
           sourcing, assessing, approving, hiring, or supervising that role.
        </p>

        <p>This includes:</p>

        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Recruiters or Talent Acquisition employees responsible for the vacancy.",
            "Hiring managers responsible for the role.",
            "Employees directly involved in the final hiring decision.",
            "Employees who will directly supervise the referred candidate.",
            "Senior managers or executives who directly influence the selection or approval of the vacancy.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p>
          You're welcome to refer candidates for roles in another team, department, office, function, or client
          account, as long as you have no direct or indirect say in the hiring decision.
        </p>
      </div>
    </>
  ),
},
{
  sectionId: "candidate",
  sectionTitle: "4. Candidate Eligibility",
  searchKeywords: `
    candidate qualify eligible referral agreed validate
    essential requirements vacancy applied same role
    validly referred another employee represented recruitment
    agency third-party provider employed previous twelve
    months active consideration rehire succession internal
    talent process processed authorised partner six 12 6
    successfully complete required pre-employment accept offer
    commences employment active processing includes direct
    sourcing contact submission application assessment examination
    participation interview hiring manager operations client
    shortlisting endorsement internal database resume actively
    stored validation guarantee shortlist outcome fair résumé
    confidential merit-based applicant  currently
  `,
  sectionBody: (
    <>
      <div className="space-y-4 text-sm leading-relaxed">
        <p>A candidate can qualify as an eligible referral when they:</p>

        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Has agreed to be referred.",
            "Meet the essential requirements of the vacancy",
            "Haven't already applied for the same role.",
            "Haven't already been validly referred by another employee.",
            "Aren't currently represented to Hyvid by a recruitment agency or third-party provider.",
            "Haven't been employed by Hyvid during the previous twelve (12) months..",
            "Aren't under active consideration through a rehire, succession, or internal talent process.",
            "Haven't been actively processed by Hyvid or an authorised recruitment partner during the previous six (6) months.",
            "Successfully complete the required recruitment and pre-employment process.",
            "Accept an employment offer and commence employment with Hyvid.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p>For this policy, active processing includes:</p>
        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Direct sourcing or contact by Talent Acquisition.",
            "Submission of an application.",
            "Completion of an assessment or examination.",
            "Participation in a Talent Acquisition, hiring manager, operations, or client interview.",
            "Shortlisting or endorsement for an internal or client vacancy.",
            "Active representation by an authorised recruitment agency.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>


        <p>
          A candidate whose résumé is already in our database, but who hasn't been actively processed in the
          previous six (6) months, may still count as an eligible referral — Talent Acquisition will validate this case
          by case.
        </p>
        <p>Keep in mind: a referral doesn't guarantee an interview, shortlist, offer, or hiring outcome.</p>
        <p>Every referred candidate goes through the same fair, confidential, and merit-based process as any other
           applicant.
        </p>
      </div>
    </>
  ),
},
{
  sectionId: "submission",
  sectionTitle: "5. Referral Submission",
  searchKeywords: `
    employees submit referrals approved channel
    communicated people culture talent acquisition
    hyvid one referral form designated email 
    company careers website internal platform submitted
    before candidate applies enters recruitment process
    directly identify referring employee full name
    application initial interview contact details
    position updated resume professional profile suitability
    relationship confirmation agreed disclosure personal family
    business reporting informal mentions chat text
    message social media verbal discussion establish
    ownership formally recorded channel records determine
    validly submission
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>
        You'll submit referrals through an approved referral channel shared by People & Culture or Talent
        Acquisition.
      </p>

      <p>Approved channels may include:</p>

      <ul className="list-none space-y-1.5 pl-2">
        {[
          "Hyvid One",
          "An employee referral form.",
          "A designated referral email address.",
          "The company careers website.",
          "Another approved internal platform.",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <p>When you submit a referral, please include:</p>

      <ul className="list-none space-y-1.5 pl-2">
        {[
          "Candidate’s full name",
          "Contact details",
          "Position being referred for",
          "Updated resume or professional profile",
          "A brief note on why you think they're a great fit",
          "Your relationship to the candidate",
          "Confirmation that the candidate has agreed to be referred",
          "Disclosure of any personal, family, business, or reporting relationship.",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <p>Submit your referral before the candidate applies or enters the recruitment process.</p>
      <p>If a candidate applies directly, they'll need to name you as the referring employee:</p>
       <ul className="list-none space-y-1.5 pl-2">
        {[
          "In the application form; or",
          "During the initial Talent Acquisition interview.",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <p>
        A quick heads-up: mentioning a referral informally — over chat, text, social media, email, or conversation
        — won't establish referral ownership. It only counts once it's formally recorded through an approved
        channel
      </p>
      <p>Talent Acquisition's records determine whether a referral was validly submitted.</p>
    </div>
  ),
},

{
  sectionId: "Ownership",
  sectionTitle: "6. Referral Validity and Ownership",
  searchKeywords: `
    valid referral remains active six 6 months
    formally received date hired eligible position
    period original referring employee applicable reward
    same candidate referred multiple employees ownership
    normally awarded complete valid referral first
    submission timestamps application records applicant tracking
    system confirmation previous recruitment activity evidence
    consent incomplete informal submissions entered process
    establish validity expires leaves before hired
    non-eligible position invalid requests relationship removed
    re-engaged independent source final talent acquisition
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>A valid referral stays active for six (6) months from the date it's formally received.</p>
      <p>
         If your candidate is hired into a different eligible position within that window, you may still qualify for the
         applicable reward.    
      </p>
      <p>
         If more than one employee refers the same candidate, referral ownership normally goes to whoever's
         complete, valid referral was received first.
      </p>

      <p>We determine ownership using:</p>

       <ul className="list-none space-y-1.5 pl-2">
        {[
          "Referral submission dates and timestamps.",
          "Candidate application records.",
          "Applicant Tracking System records.",
          "Candidate confirmation.",
          "Previous recruitment activity.",
          "Evidence of candidate consent.",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <p>
         Incomplete referrals, informal submissions, or referrals submitted after the candidate has already entered
         the recruitment process won't establish ownership.
      </p>

      <p>Referral ownership ends when:</p>

       <ul className="list-none space-y-1.5 pl-2">
        {[
          "The six-month validity period expires.",
          "You leave Hyvid before the candidate is hired.",
          "The candidate is hired into a non-eligible position.",
          "The referral is found to be invalid.",
          "The candidate asks that the referral relationship be removed.",
          "The candidate is re-engaged after the validity period through an independent recruitment source.",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <p>Talent Acquisition makes the final call on referral validity and ownership.</p>
    </div>
  ),
},

{
  sectionId: "rewards",
  sectionTitle: "7. Referral Rewards",
  searchKeywords: `
    referral reward levels role classifications
    applicable amounts maintained separate rewards matrix
    communicated eligible vacancy advertised announced
    seniority technical complexity candidate scarcity
    market availability recruitment urgency business criticality
    client requirements expected difficulty filling
    talent acquisition temporary campaign incentives selected
    roles clients departments locations hiring periods
    high-volume requirements special terms announced before
    submitted retrospectively standard differ specific
    campaign apply
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>
        Referral reward levels, role classifications, and applicable amounts are maintained in the separate <strong>Employee Referral Rewards Matrix.</strong>
      </p>
      <p>
        We'll share the applicable reward when an eligible vacancy is advertised or announced.
      </p>
      <p>Reward levels may reflect:</p>
      <ul className="list-none space-y-1.5 pl-2">
        {[
          "Seniority",
          "Technical complexity",
          "Candidate scarcity",
          "Market availability",
          "Recruitment urgency",
          "Business criticality",
          "Client requirements",
          "Expected difficulty of filling the role",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <p>From time to time, Talent Acquisition may introduce temporary or campaign-based incentives for selected:</p>
      <ul className="list-none space-y-1.5 pl-2">
        {[
          "Roles",
          "Clients",
          "Departments",
          "Locations",
          "Hiring periods",
          "High-volume requirements",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
      <p>
         Any special campaign terms will be announced before you submit a referral, and won't normally apply
         retroactively.
      </p>
      <p>
         Where an approved campaign's terms differ from the standard Rewards Matrix, the campaign terms
         apply.
      </p>
    </div>
  ),
},
{
  sectionId: "payment",
  sectionTitle: "8. Payment Structure",
  searchKeywords: `
    stated matrix approved campaign announcement
    reward paid equal installments first thirty
    percent payable commencement employment onboarding process
    remaining seventy completes three calendar months
    continuous service processed next practicable payroll
    following validation relevant milestone subject confirmation
    status validation cut-off dates finance management
    approval complete documentation applicable processing requirements
    gross amounts taxes statutory deductions withholding
    basic salary guaranteed compensation regular allowances
    retirement benefits separation overtime premium calculations
    payment structure Referral Rewards 3
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>
        Unless the Employee Referral Rewards Matrix or an approved campaign says otherwise, your referral
        reward is paid in two installments:
      </p>
       <div className="flex gap-3 p-3 rounded-lg border bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700">
        <ul className="list-none space-y-1.5 pl-2">
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>
                  <strong>First 30%: </strong>Payable once your referred candidate successfully starts and completes onboarding
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>
                  <strong>Remaining 70%: </strong>Payable after your referred candidate completes three (3) calendar months of
                  continuous service.
              </span>
            </li>
      </ul>
      </div>

      <p>
        We'll process payment through the next practicable payroll run after we validate the relevant milestone.
      </p>

      <p>Payment remains subject to:</p>
      <ul className="list-none space-y-1.5 pl-2">
        {[
          "Confirmation of employment status",
          "Referral validation",
          "Payroll cut-off dates",
          "Finance and management approval",
          "Complete referral documentation",
          "Applicable payroll processing requirements",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <p>
        All referral rewards are gross amounts and are subject to applicable
        taxes, statutory deductions, and payroll withholding.
      </p>

      <p>They don't form part of your:</p>
      <ul className="list-none space-y-1.5 pl-2">
        {[
          "Basic salary",
          "Guaranteed compensation",
          "Regular allowances",
          "Retirement benefits",
          "Separation pay",
          "Overtime or premium-pay calculations",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  ),
},

{
  sectionId: "conditions",
  sectionTitle: "9. Conditions for Payment",
  searchKeywords: `
    reward payable validly submitted candidate
    entered recruitment hired eligible vacancy
    applicable role level formally approved
    referred employee remains actively employed milestone
    referring remains validated people culture talent
    acquisition fraud misrepresentation conflict interest breach
    required approval payroll completed conditions
    approved leave eligibility provided payment
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>Your referral reward is only payable when:</p>

       <ul className="list-none space-y-1.5 pl-2">
        {[
          "The referral was validly submitted before the candidate entered the recruitment process.",
          "The candidate was hired into an eligible vacancy.",
          "The applicable role and reward level were formally approved.",
          "The referred employee remains actively employed on the applicable milestone date.",
          "You're still actively employed on the relevant milestone date.",
          "People & Culture and Talent Acquisition have validated the referral.",
          "No fraud, misrepresentation, conflict of interest, or policy breach has come up.",
          "All required approval and payroll processes have been completed.",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <p>If you're on approved leave, you remain eligible, provided you're still actively employed.</p>
    </div>
  ),
},

{
  sectionId: "separation",
  sectionTitle: "10. Separation from Employment",
  searchKeywords: `
    leaves before reaches commencement milestone
    unpaid installment forfeited separation
    earned separates payroll payment date
    dismissed fraud misconduct misuse
    later invalid breach law company policy
    processed final pay standard procedures
    resigns dismissed payment milestone
    related installment payable employment
    already paid recovered later clawed
  `,
  sectionBody: (
    <div>
      <div className="space-y-4 text-sm leading-relaxed">
        <div className="flex gap-3 p-3 rounded-lg border bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-700">
          <CircleCheckBig className="w-4 h-4 text-emerald-500 dark:text-emerald-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
          <span>
            If you leave Hyvid before your referred candidate reaches the relevant milestone — successful
            commencement or the three-month mark — the unpaid installment tied to that milestone is forfeited.     
          </span>
        </div>
     
        </div>

        <p>
           Once an installment has been validly earned on its milestone date, it stays payable even if you separate
           before the actual payroll date — unless:
        </p>

        <ul className="list-none space-y-1.5 pl-2">
          {[
            "You were dismissed for fraud, misconduct, or misuse of the referral program.",
            "The referral was later found to be invalid.",
            "Payment would breach applicable law or company policy.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p>An earned but unpaid installment may be processed through 
           payroll or final pay, subject to the company’s
           standard payroll procedures.
        </p>

        <p>If your referred candidate resigns or is dismissed before reaching a payment milestone, that installment
          won't be payable. 
        </p>

        <p>And an installment that's already been validly earned and paid won't normally be clawed back just because
           the referred employee later leaves the company.
        </p>
      </div>
    </div>
  ),
},

{
  sectionId: "confirmation",
  sectionTitle: "11. Referral Confirmation",
  searchKeywords: `
    candidate commences employment validated
    talent acquisition notify referring
    confirmed start eligibility status reward range
    total gross qualifying milestones expected payroll
    timing indicative validation cut-offs approval
  `,
  sectionBody: (
    <div>
      <div className="space-y-4 text-sm leading-relaxed">
        <p>Once your referred candidate starts and the referral is validated, Talent Acquisition will let you know:</p>

        <ul className="list-none space-y-1.5 pl-2">
          {[
            "The candidate’s confirmed start.",
            "The referral eligibility status.",
            "The applicable reward range.",
            "The total gross reward.",
            "The qualifying payment milestones.",
            "The expected payroll timing.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p>Payment dates we share are indicative and remain subject to validation, payroll cut-offs, and approval
           requirements.
        </p>
      </div>
    </div>
  ),
},

{
  sectionId: "privacy",
  sectionTitle: "12. Confidentiality and Privacy",
  searchKeywords: `
    permission submitting resume personal information
    confidential recruitment selection disclosure disclose
    interview feedback assessment results background-check
    reference-check salary discussions rejection deliberations
    sensitive candidate updates received eligibility detailed
    ineligible application process hired reward milestone
    privacy data-protection requirements confidentiality
  `,
  sectionBody: (
    <div>
      <div className="space-y-4 text-sm leading-relaxed">
        <p>Before you submit a candidate's résumé or personal information, make sure you have their permission.</p>
        <p>Hyvid keeps the recruitment and selection process confidential, so we won't share detailed information
            about your candidate's application with you.
        </p>
        <p>Confidential information includes:
        </p>
        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Interview feedback",
            "Assessment results",
            "Background-check information",
            "Reference-check information",
            "Salary discussions",
            "Reasons for rejection",
            "Internal hiring deliberations",
            "Personal or sensitive candidate information",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p>You may still receive limited status updates, such as:</p>
        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Referral received",
            "Referral eligible or ineligible",
            "Application in process",
            "Candidate hired or not hired",
            "Reward milestone reached",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p>We handle candidate information in line with applicable privacy and data-protection requirements.</p>
      </div>
    </div>
  ),
},

{
  sectionId: "responsibilities",
  sectionTitle: "13. Employee Responsibilities",
  searchKeywords: `
    consent honestly responsibly
    vacancy requirements accurate complete information
    conflict interest confidentiality
    pressuring recruiters interviewers clients managers
    requesting feedback promising interviews salaries offers
    outcomes official process authorised representatives
    compensation employment terms hiring decisions
  `,
  sectionBody: (
    <div>
      <div className="space-y-4 text-sm leading-relaxed">
        <p>As a participant in the program, you're expected to:</p>
        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Get the candidate's consent.",
            "Refer candidates honestly and responsibly.",
            "Review the vacancy requirements before making a referral.",
            "Provide accurate and complete information.",
            "Disclose any actual or potential conflict of interest.",
            "Respect the confidentiality of the recruitment process.",
            "Avoid pressuring recruiters, interviewers, clients, or hiring managers.",
            "Avoid requesting confidential candidate feedback.",
            "Avoid promising interviews, salaries, offers, or employment outcomes.",
            "Follow the official referral process.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p>Only authorised company representatives may communicate formal 
           offers, compensation details, employment terms, and final 
           hiring decisions.
        </p>
      </div>
    </div>
  ),
},

{
  sectionId: "misuse",
  sectionTitle: "14. Program Misuse",
  searchKeywords: `
    conduct disqualification forfeiture unpaid rewards
    recovery incorrect payments disciplinary action
    consent false misleading information profiles
    manipulating submission dates records
    bypassing official process
    confidential interview questions assessment materials
    interfering pressuring recruitment team
    contractual client supplier non-solicitation obligations
    integrity suspected fraud falsification circumvention
    manipulation investigated code conduct procedures
    violations dismissal due process applicable law
  `,
  sectionBody: (
    <div>
      <div className="space-y-4 text-sm leading-relaxed">
        <p>The following conduct may result in referral 
           disqualification, forfeiture of unpaid rewards, 
           recovery of incorrect payments, and disciplinary action:
        </p>
        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Submitting a referral without candidate consent.",
            "Providing false or misleading information.",
            "Creating false candidate profiles.",
            "Manipulating submission dates or recruitment records.",
            "Attempting to claim a candidate already known to or processed by the company.",
            "Deliberately bypassing the official referral process.",
            "Disclosing confidential interview questions or assessment materials.",
            "Interfering with or pressuring the recruitment team.",
            "Referring candidates in breach of contractual, client, supplier, or non-solicitation obligations.",
            "Any conduct that compromises the integrity of the program.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p>
           We'll investigate suspected fraud, falsification, deliberate circumvention, or manipulation in line with
           Hyvid's Code of Conduct and disciplinary procedures.
        </p>
        <p>
           Confirmed violations may lead to disciplinary action up to and including dismissal, subject to due process
           and applicable law.
        </p>
      </div>
    </div>
  ),
},

{
  sectionId: "overpayments",
  sectionTitle: "15. Overpayments",
  searchKeywords: `
    incorrect duplicate excessive payment correction
    overpayments People Culture Talent Acquisition
    repayment process finance coordination arrange
    promptly believe received coordinate employee occurs
  `,
  sectionBody: (
    <div>
      <div className="space-y-4 text-sm leading-relaxed">
        <p>If an incorrect, duplicate, or excessive referral payment happens, People & Culture, Talent Acquisition,
           and Finance will work with you to arrange an appropriate correction or repayment.
        </p>

        <p>If you believe you've received an incorrect payment, please let People & Culture, Talent Acquisition, or
           Finance know right away.
        </p>
      </div>
    </div>
  ),
},

{
  sectionId: "arrangements",
  sectionTitle: "16. Transitional Arrangements",
  searchKeywords: `
    effective date validly submitted referrals
    reward amount payment conditions communicated
    submission current matrix campaign terms applicable
    governed
  `,
  sectionBody: (
    <div>
      <div className="space-y-4 text-sm leading-relaxed">
        <p>
           Referrals validly submitted before this policy's effective date will continue to follow the reward amount
           and payment conditions that applied when they were submitted.
        </p>

        <p>Referrals submitted on or after the effective date are governed by:</p>

         <ul className="list-none space-y-1.5 pl-2">
          {[
            "This policy",
            "The current Employee Referral Rewards Matrix",
            "Any approved special campaign terms applicable at the time of submission",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  ),
},

{
  sectionId: "review",
  sectionTitle: "17. Policy Review",
  searchKeywords: `
    policy review annually significant changes
    recruitment practices program requirements
    payroll finance processes legal privacy
    business client needs amend suspend replace
    discontinue
  `,
  sectionBody: (
    <div>
      <div className="space-y-4 text-sm leading-relaxed">
        <p>We'll review this policy at least annually, and whenever there are significant changes to:</p>

         <ul className="list-none space-y-1.5 pl-2">
          {[
            "Recruitment practices",
            "Referral program requirements",
            "Payroll or finance processes",
            "Legal or privacy requirements",
            "Business or client needs",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p>Hyvid reserves the right to amend, suspend, replace, or discontinue the Employee Referral Program at any time.</p>

      </div>
      <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/20">
        <p className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
          Got questions? Reach out to:
        </p>

        <div className="space-y-1 text-sm">
          <p className="text-[14px] font-semibold muted-text">
            Talent Acquisition
          </p>

          <p>
            <span className="font-medium muted-text">
              Email:
            </span>{" "}
            <span className="italic underline text-gray-600 dark:text-gray-400">talent@hyvid.com</span>
          </p>

          <p>
            <span className="font-medium muted-text">
              Referral Portal:
            </span>{" "}
            <span className="text-gray-600 dark:text-gray-400">HyvidOne</span>
          </p>
        </div>
      </div>
    </div>
  ),
},
];

const EmployeeReferralProgramPolicyContents = ({ searchTerm }) => {
  const filteredSections = sections.filter((section) => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    
    return (
      section.sectionTitle.toLowerCase().includes(search) ||
      section.searchKeywords?.toLowerCase().includes(search)
    );
  });
  return (
    <ContentsContainer sections={filteredSections} />
  );
};

export default EmployeeReferralProgramPolicyContents;